
import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  DOCUMENT
} from '@angular/core';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { TimeoutService } from '@df/ng/timeout.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NOffClick } from './off-click.config';
import { OffClickService } from './off-click.service';

/**
 * Off click directiv is used to track when user click outside of some element.
 *
 * ```html
 * <div (offClick)="someAction()"
 *      offClickFilter=".off-click-ignore">Off click body</div>
 *
 * <div class="off-click-ignore">Off click execption</div>
 * ```
 *
 * @output offClick - Pass function to call there.
 *
 * @input offClickOff - Use it to disable off click completely - you should always set it, off click processing is bit heavy.
 * @input offClickFilter - Pass css style selector for elments to be ignored.
 *
 * @author Maurycy Ornat maurycy@tomandco.co.uk
 */

declare global {
    export interface Event {
        preventOffClick: boolean;
    }
}

@Directive({
    selector: '[offClick]',
    standalone: true
})
export class OffClickDirective implements OnChanges, OnDestroy, OnChanges, OnInit {
    private _cleanup?: () => void;

    private syncSubscription?: () => void;

    private stateSubscription?: Subscription;

    // add variable to detect touch users moving.
    protected _touchMove = false;

    private get isGlobal(): boolean {
        return (this.config?.global && !this.offClickInApp) || this.offClickInCms || false;
    }

    private rootElement?: HTMLElement | Document;

    /**
     */
    constructor(
        private elementRef: ElementRef,
        private timeoutService: TimeoutService,
        private client: Client,
        private offClickService: OffClickService,
        @Inject(DOCUMENT) private document: Document,
        @Optional() @Inject(NOffClick.CONFIG) private config: NOffClick.IConfig,
        private app: App
    ) {}

    /**
     * Method to call on off click.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Output()
    offClick: EventEmitter<any> = new EventEmitter();

    /**
     * Flag disabling checking.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input()
    offClickOff?: boolean;

    @Input()
    offClickState?: BehaviorSubject<boolean>;

    /** Force off click in CMS also */
    @Input()
    offClickGlobal?: boolean;

    /**
     * Optional selector excliding elements from event trigger.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input()
    offClickFilter?: string;

    /**
     * Optional selector for include elements from event trigger.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input()
    offClickIncludedFilter?: string;

    @Input()
    offClickInCms?: boolean;

    @Input()
    offClickInApp?: boolean;

    /**
     * If `offClickOff` flag is `false` then subscribe to document click events.
     *
     * @author Rafał Wolak <rafal@tomandco.co.uk>
     * @param {SimpleChanges} changes
     * @memberof OffClickDirective
     */
    ngOnInit(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        this.rootElement = this.isGlobal ? this.document : this.client.getRootElement();

        this.syncSubscription = this.timeoutService.debounce(() => {
            if (!this.offClickOff) {
                if (!this._cleanup) {
                    const scope = this.isGlobal ? this.document : this.client.scrollElementForEvents;
                    scope.addEventListener(
                        'touchmove',
                        (event: Event) => {
                            this.clickHandler(this.offClickService.clickTarget || event.target!, event);
                        },
                        { passive: true }
                    );

                    const onEvent = (event: Event) => {
                        this.clickHandler(this.offClickService.clickTarget || event.target!, event);
                    };

                    const cleanup: (() => void)[] = [];
                    ['touchmove', 'touchend', 'click'].forEach(eventName => {
                        scope.addEventListener(eventName, onEvent, { passive: true });
                        cleanup.push(() => scope.removeEventListener(eventName, onEvent));
                    });
                    this._cleanup = () => cleanup.forEach(c => c());
                }
            } else {
                if (this._cleanup) {
                    this._cleanup();

                    this._cleanup = undefined;
                }
            }
        }, 200);
        this.syncSubscription();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['offClickState']) {
            this.stateSubscription?.unsubscribe();

            this.stateSubscription = this.offClickState?.subscribe(state => {
                this.offClickOff = !state;
                if (this.syncSubscription) {
                    this.syncSubscription();
                }
            });
        }

        if (this.syncSubscription) {
            this.syncSubscription();
        }
    }

    /**
     * Detache view to force variable updates.
     * Unsubscribe all observables.
     *
     * @author Rafał Wolak <rafal@tomandco.co.uk>
     * @memberof OffClickDirective
     */
    ngOnDestroy(): void {
        if (this._cleanup) {
            this._cleanup();

            this._cleanup = undefined;
        }

        this.stateSubscription?.unsubscribe();
    }

    /**
     * Checks if target is off the element, respecting offClickIf and offClickFilter.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {EventTarget} targetElement
     * @param  {Event} event
     * @return {boolean}
     */
    protected clickHandler(targetElement: EventTarget, event: Event): void {
        // If event is a touchmove adjust touchMove state
        if (event.type === 'touchmove') {
            this._touchMove = true;
            // And end function
            return;
        }
        // This will always fire on the touchend after the touchmove runs
        if (this._touchMove) {
            // Reset touchmove to false
            this._touchMove = false;
            // And end function
            return;
        }

        if (event.preventOffClick || event.defaultPrevented) {
            return;
        }
        // if in click do nothing;
        if (this.elementRef.nativeElement.contains(targetElement)) {
            return;
        }

        //  if clicked element does not exist anymore or is not part of the scope ignore it
        if (!this.rootElement!.contains(targetElement as HTMLElement)) {
            return;
        }

        // if click filtered element do nothing
        if (this.offClickFilter) {
            const offClickFilter = this.document.querySelectorAll(this.offClickFilter);

            if (this.targetInFilter(targetElement, offClickFilter)) {
                return;
            }
        }
        if (this.offClickIncludedFilter) {
            const offClickFilter = this.document.querySelectorAll(this.offClickIncludedFilter);

            if (!this.targetInFilter(targetElement, offClickFilter)) {
                return;
            }
        }
        this.offClick.emit(event);

        return;
    }

    /**
     * Helper function checking if element is where filterd element is placed.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    protected targetInFilter(target: EventTarget, elms: NodeListOf<Element>) {
        const elmsLen = elms.length;

        for (let i = 0; i < elmsLen; ++i) {
            const currentElem = elms[i];
            let containsTarget = false;

            try {
                containsTarget = currentElem.contains(<Node>target);
            } catch (e) {
                // If the node is not an Element (e.g., an SVGElement) node.contains() throws Exception in IE,
                // see https://connect.microsoft.com/IE/feedback/details/780874/node-contains-is-incorrect
                // In this case we use compareDocumentPosition() instead.
                if (typeof currentElem.compareDocumentPosition !== 'undefined') {
                    // tslint:disable:no-bitwise
                    containsTarget = currentElem === target || Boolean(currentElem.compareDocumentPosition(<Node>target) & 16);
                    // tslint:enable:no-bitwise
                }
            }

            if (containsTarget) {
                return true;
            }
        }

        return false;
    }
}
